import { FoodLibraryMeal } from "@/clients/models/FoodLibraryMeal";
import { BackIcon } from "@/components/icons/back-icon";
import { ChatIconSquare } from "@/components/icons/chat-icon-square";
import { CloseIcon } from "@/components/icons/close-icon";
import { FavouritesOutlineIcon } from "@/components/icons/favourites-outline-icon";
import { GearIcon } from "@/components/icons/gear-icon";
import { HelpIcon } from "@/components/icons/help-icon";
import { LightningBoltIcon } from "@/components/icons/lightning-bolt-icon";
import { MembershipIcon } from "@/components/icons/membership-icon";
import { SearchIcon } from "@/components/icons/search-icon";
import { SoupIcon } from "@/components/icons/soup-icon";
import { StarIcon } from "@/components/icons/star-icon";
import { TextButton } from "@/components/ui/buttons";
import IconButton from "@/components/ui/buttons/IconButton";
import { MealDetailModal } from "@/features/ai-chat/components/meal-detail-modal";
import { useOpenCoachChat } from "@/features/ai-chat/hooks/use-open-coach-chat";
import HelpScreen from "@/features/ai-chat/pages/help-screen";
import { FoodLibraryList } from "@/features/meal-plan/components/food-library-list";
import {
  SAVED_MEALS_TAB_OPTIONS,
  SavedMealsTabValue,
} from "@/features/meal-plan/constants/saved-meals-tabs";
import ChangePasswordPage from "@/features/user-profile/pages/change-password";
import DeleteAccountPage from "@/features/user-profile/pages/delete-account";
import AppSettingsPage from "@/features/user-profile/pages/app-settings";
import DietaryRequirementPage from "@/features/user-profile/pages/dietary-requirement";
import FavoriteCuisinesPage from "@/features/user-profile/pages/favorite-cuisines";
import FoodAvoidsPage from "@/features/user-profile/pages/food-avoids";
import PersonalInfoPage from "@/features/user-profile/pages/personal-info";
import PreferencesPage from "@/features/user-profile/pages/preferences";
import SubscriptionManagementPage from "@/features/user-profile/pages/subscription-management";
import SubscriptionUpgradePage from "@/features/user-profile/pages/subscription-upgrade";
import SupermarketsPage from "@/features/user-profile/pages/supermarkets";
import { openContactEmail } from "@/features/user-profile/utils/support-links";
import { useGetOtherPreferences } from "@/hooks/requests/other-preferences/use-get-other-preferences";
import { useUpdateOtherPreferences } from "@/hooks/requests/other-preferences/use-update-other-preferences";
import { useGetSubscriptionStatus } from "@/hooks/requests/user/use-get-subscription-status";
import { useLogout } from "@/hooks/use-logout";
import { SettingModalHeader } from "@/features/user-profile/components/setting-modal-header";
import { pendingChatFoodAtom } from "@/stores/chat.store";
import { cn } from "@/utils/cn";
import { openAppRating } from "@/utils/open-app-rating";
import { IonContent, IonNav, IonPage } from "@ionic/react";
import { settingsNavResetAtom } from "@/stores/settings-nav.store";
import { BACK_BUTTON_PRIORITY, useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MemoryRouter, Route, Switch, useHistory } from "react-router-dom";
import { SettingsRow } from "../../components/settings-row";

function SectionHeader({ title }: { title: string }) {
  return <p className="pl-4 text-[13px] font-bold text-[#1d1232]">{title}</p>;
}

// --- Pages pushed onto the IonNav stack ---

function ProfileNavPage({ onBack }: { onBack: () => void }) {
  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="h-full overflow-y-auto">
          <PersonalInfoPage onBack={onBack} />
        </div>
      </IonContent>
    </IonPage>
  );
}

function AppSettingsContent({ onBack }: { onBack: () => void }) {
  const history = useHistory();
  return (
    <Switch>
      <Route exact path="/" render={() => <AppSettingsPage onBack={onBack} />} />
      <Route
        path="/change-password"
        render={() => <ChangePasswordPage onBack={() => history.goBack()} />}
      />
      <Route
        path="/delete-account"
        render={() => <DeleteAccountPage onBack={() => history.goBack()} />}
      />
    </Switch>
  );
}

function AppSettingsNavPage({ onBack }: { onBack: () => void }) {
  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="h-full overflow-y-auto">
          <MemoryRouter initialEntries={["/"]}>
            <AppSettingsContent onBack={onBack} />
          </MemoryRouter>
        </div>
      </IonContent>
    </IonPage>
  );
}

function PreferencesContent({ onBack }: { onBack: () => void }) {
  const history = useHistory();
  return (
    <Switch>
      <Route exact path="/" render={() => <PreferencesPage onBack={onBack} />} />
      <Route
        path="/supermarkets"
        render={() => <SupermarketsPage onBack={() => history.goBack()} />}
      />
      <Route
        path="/food-avoids"
        render={() => <FoodAvoidsPage onBack={() => history.goBack()} />}
      />
      <Route
        path="/cuisines"
        render={() => <FavoriteCuisinesPage onBack={() => history.goBack()} />}
      />
      <Route
        path="/dietary-requirement"
        render={() => <DietaryRequirementPage onBack={() => history.goBack()} />}
      />
    </Switch>
  );
}

function PreferencesNavPage({ onBack }: { onBack: () => void }) {
  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="h-full overflow-y-auto">
          <MemoryRouter initialEntries={["/"]}>
            <PreferencesContent onBack={onBack} />
          </MemoryRouter>
        </div>
      </IonContent>
    </IonPage>
  );
}

// IonNav.push() calls component functions outside React's rendering system,
// so hooks cannot live in SubscriptionNavPage itself. State lives here instead.
function SubscriptionNavPageContent({ onBack }: { onBack: () => void }) {
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);

  // Android back closes the upgrade overlay before the IonNav (10) pops.
  useCloseOnBackButton(isUpgradeOpen, () => setIsUpgradeOpen(false), {
    priority: BACK_BUTTON_PRIORITY.fullScreenLayer,
  });

  return (
    <>
      <IonContent scrollY={false}>
        <div className="h-full overflow-y-auto">
          <SubscriptionManagementPage onBack={onBack} onUpgrade={() => setIsUpgradeOpen(true)} />
        </div>
      </IonContent>
      {/* Portal to body — escapes IonPage CSS transform stacking context */}
      {createPortal(
        <div
          className={cn(
            "fixed inset-0 z-[1000] overflow-y-auto bg-[#fcfbfb]",
            "transition-transform duration-300 ease-out",
            isUpgradeOpen ? "translate-y-0" : "translate-y-full",
          )}
          aria-hidden={!isUpgradeOpen}
        >
          <SubscriptionUpgradePage
            isVisible={isUpgradeOpen}
            onBack={() => setIsUpgradeOpen(false)}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

function SubscriptionNavPage({ onBack }: { onBack: () => void }) {
  return (
    <IonPage>
      <SubscriptionNavPageContent onBack={onBack} />
    </IonPage>
  );
}

function HelpNavPage({ onBack }: { onBack: () => void }) {
  return <HelpScreen onBack={onBack} standalone />;
}

function FoodsAddedContent({
  onBack,
  onAddMeal,
}: {
  onBack: () => void;
  onAddMeal?: (meal: FoodLibraryMeal) => void;
}) {
  const [selectedTab, setSelectedTab] = useState<SavedMealsTabValue>("your-food");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMeal, setSelectedMeal] = useState<FoodLibraryMeal | null>(null);

  return (
    <div className="relative flex h-full flex-col">
      <div className="safe-area-top bg-[#f4f2fd]">
        <div className="flex items-center px-2 pt-3 pb-[9px]">
          <IconButton
            aria-label="Back"
            onClick={onBack}
            className="bg-transparent hover:bg-black/5 active:bg-black/5"
          >
            <BackIcon className="stroke-[#620ad5]" />
          </IconButton>
          <div className="flex flex-1 items-center justify-center">
            <span className="text-[18px] font-bold">Foods Added</span>
          </div>
          <div aria-hidden className="h-11 w-11" />
        </div>
        <div className="flex items-center px-4">
          {SAVED_MEALS_TAB_OPTIONS.map((tab) => {
            const isActive = selectedTab === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => {
                  setSelectedTab(tab.value as SavedMealsTabValue);
                  setSearchQuery("");
                }}
                className="flex h-11 flex-1 flex-col items-center justify-end"
              >
                <div className="relative flex flex-col items-center pt-2 pb-1">
                  <span
                    className={cn(
                      "mb-[14px] text-sm font-bold",
                      isActive ? "text-os-brand" : "text-gray-500",
                    )}
                  >
                    {tab.label}
                  </span>
                  <div
                    className={cn(
                      "absolute -right-3 bottom-0 -left-3 h-1 rounded",
                      isActive ? "bg-os-brand" : "bg-transparent",
                    )}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-[22px] px-4">
        <div className="focus-within:ring-os-brand flex h-11 items-center gap-2 rounded-[14px] bg-[#f4f2fd] pr-2 pl-3 ring-1 ring-transparent transition-shadow">
          <SearchIcon className="h-5 w-5 shrink-0 fill-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search foods, meals or brands"
            className="flex-1 bg-transparent text-sm font-medium text-gray-950 outline-none placeholder:text-gray-500 focus:placeholder:opacity-60"
          />
          {searchQuery && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchQuery("")}
              className="bg-os-brand flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
            >
              <CloseIcon className="h-3 w-3 fill-white text-white" />
            </button>
          )}
        </div>
      </div>
      <div className="no-scrollbar flex-1 overflow-y-auto px-4 pt-4 pb-6">
        <FoodLibraryList
          tab={selectedTab}
          searchQuery={searchQuery}
          onClearSearch={() => setSearchQuery("")}
          variant="flat"
          onMealClick={setSelectedMeal}
          renderMealAction={
            onAddMeal
              ? (meal) => {
                  if (!meal.id) return null;
                  return (
                    <TextButton
                      type="button"
                      aria-label={`Add ${meal.name}`}
                      onClick={() => onAddMeal(meal)}
                      className="text-os-brand h-10 w-auto shrink-0 px-2 text-[14px]"
                    >
                      Add
                    </TextButton>
                  );
                }
              : undefined
          }
        />
      </div>
      <MealDetailModal
        meal={selectedMeal}
        isOpen={selectedMeal !== null}
        onDismiss={() => setSelectedMeal(null)}
        onMealUpdated={setSelectedMeal}
      />
    </div>
  );
}

function FoodsAddedNavPage({
  onBack,
  onAddMeal,
}: {
  onBack: () => void;
  onAddMeal?: (meal: FoodLibraryMeal) => void;
}) {
  return (
    <IonPage>
      <IonContent scrollY={false}>
        <FoodsAddedContent onBack={onBack} onAddMeal={onAddMeal} />
      </IonContent>
    </IonPage>
  );
}

// --- Root page of the IonNav stack (the settings list) ---

function SettingsRoot({ navRef }: { navRef: React.RefObject<HTMLIonNavElement> }) {
  const { subscriptionStatus } = useGetSubscriptionStatus({ enabled: true });
  const membershipLabel: Record<string, string> = {
    monthly: "Monthly",
    yearly: "Yearly",
    free: "Free",
  };
  const membershipValue =
    membershipLabel[subscriptionStatus?.subscription_type?.toLowerCase() ?? ""] ?? undefined;

  const { otherPreferences, isLoading: isLoadingPreferences } = useGetOtherPreferences();
  const { updateOtherPreferences, isUpdating: isUpdatingPreferences } = useUpdateOtherPreferences();
  const caloriesAndMacrosEnabled = otherPreferences?.showCaloriesAndMacros ?? false;

  const setPendingChatFood = useSetAtom(pendingChatFoodAtom);
  const openCoachChat = useOpenCoachChat();
  const { logout } = useLogout();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const push = (Component: React.ComponentType<{ onBack: () => void }>) => {
    navRef.current?.push(Component, { onBack: () => navRef.current?.pop() });
  };

  return (
    <IonPage style={{ backgroundColor: "#fcfbfb" }}>
      <IonContent scrollY={false} style={{ "--background": "#fcfbfb" } as React.CSSProperties}>
        <div data-scroll-container className="no-scrollbar h-full overflow-y-auto">
          <SettingModalHeader title="Settings" />
          <div className="mx-auto flex w-full max-w-[450px] flex-col gap-4 px-4 pt-4 pb-28">
            {/* Foods */}
            <div className="flex flex-col gap-2">
              <SectionHeader title="Foods" />
              <div className="flex flex-col gap-3">
                <SettingsRow
                  icon={<FavouritesOutlineIcon />}
                  label="Foods Added"
                  right={{ type: "chevron" }}
                  onClick={() =>
                    navRef.current?.push(FoodsAddedNavPage, {
                      onBack: () => navRef.current?.pop(),
                      onAddMeal: (meal: FoodLibraryMeal) => {
                        navRef.current?.pop();
                        setPendingChatFood(meal);
                        openCoachChat();
                      },
                    })
                  }
                />
                <SettingsRow
                  icon={<SoupIcon className="h-5 w-5 fill-current" />}
                  label="Food Preferences"
                  right={{ type: "chevron" }}
                  onClick={() => push(PreferencesNavPage)}
                />
              </div>
            </div>

            {/* Settings */}
            <div className="flex flex-col gap-2">
              <SectionHeader title="Settings" />
              <div className="flex flex-col gap-3">
                <SettingsRow
                  icon={<MembershipIcon className="h-5 w-5" />}
                  label="Subscription"
                  right={{ type: "chevron", value: membershipValue }}
                  onClick={() => push(SubscriptionNavPage)}
                />
                <SettingsRow
                  icon={
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M17 9.625C17 12.6626 14.5376 15.125 11.5 15.125C8.46243 15.125 6 12.6626 6 9.625C6 6.58743 8.46243 4.125 11.5 4.125C14.5376 4.125 17 6.58743 17 9.625Z"
                        stroke="#620AD5"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 19.125C5.51328 16.7343 8.26172 15.125 11.5 15.125C14.7383 15.125 17.4867 16.7343 19 19.125"
                        stroke="#620AD5"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  label="Your Profile"
                  right={{ type: "chevron" }}
                  onClick={() => push(ProfileNavPage)}
                />
                <SettingsRow
                  icon={<GearIcon className="h-5 w-5" />}
                  label="App Settings"
                  right={{ type: "chevron" }}
                  onClick={() => push(AppSettingsNavPage)}
                />
                {/* <SettingsRow
                  icon={<LightningBoltIcon className="h-5 w-5" />}
                  label="Show Calories & Macros"
                  right={{
                    type: "toggle",
                    checked: caloriesAndMacrosEnabled,
                    onCheckedChange: (value) =>
                      updateOtherPreferences({ showCaloriesAndMacros: value }),
                    disabled: isLoadingPreferences || isUpdatingPreferences,
                  }}
                /> */}
              </div>
            </div>

            {/* Other */}
            <div className="flex flex-col gap-2">
              <SectionHeader title="Other" />
              <div className="flex flex-col gap-3">
                <SettingsRow
                  icon={<HelpIcon className="h-5 w-5" />}
                  label="Help and Support"
                  right={{ type: "chevron" }}
                  onClick={() => push(HelpNavPage)}
                />
                <SettingsRow
                  icon={<ChatIconSquare />}
                  label="Send Feedback"
                  right={{ type: "chevron" }}
                  onClick={() => openContactEmail("App feedback")}
                />
                <SettingsRow
                  icon={<StarIcon />}
                  label="Rate The App"
                  right={{ type: "chevron" }}
                  onClick={openAppRating}
                />
              </div>
            </div>

            {/* Log Out */}
            <div className="pt-2">
              <button
                type="button"
                onClick={handleLogout}
                className="flex h-[52px] w-full items-center justify-center rounded-[36px] bg-white drop-shadow-[0px_8px_32.35px_rgba(64,0,255,0.1)] active:bg-gray-50"
              >
                <span className="text-[16px] font-medium text-[#eb190a]">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

export default function SettingsAndToolsPage() {
  const navRef = useRef<HTMLIonNavElement>(null);
  const resetCount = useAtomValue(settingsNavResetAtom);
  const isFirstReset = useRef(true);

  useEffect(() => {
    if (isFirstReset.current) {
      isFirstReset.current = false;
      return;
    }
    const nav = navRef.current;
    if (!nav) return;
    // If we're in a detail view, pop back to root (smooth iOS slide).
    // If already at root (length === 1), do nothing.
    nav.getLength().then((length) => {
      if (length > 1) {
        nav.popToRoot();
      }
    });
  }, [resetCount]);

  // Intercept the hardware/swipe back so it pops the IonNav stack instead of
  // navigating the Ionic Router back to the previous app route.
  // At root (length ≤ 1) we call processNextHandler so the router can
  // navigate away normally (e.g. back to /overview).
  useEffect(() => {
    const handler = (ev: Event) => {
      (ev as CustomEvent).detail.register(10, (processNextHandler: () => void) => {
        const nav = navRef.current;
        if (!nav) {
          processNextHandler();
          return;
        }
        nav.getLength().then((length) => {
          if (length > 1) {
            nav.pop();
          } else {
            processNextHandler();
          }
        });
      });
    };
    document.addEventListener("ionBackButton", handler);
    return () => document.removeEventListener("ionBackButton", handler);
  }, []);

  return (
    <IonPage>
      <IonNav ref={navRef} root={() => <SettingsRoot navRef={navRef} />} />
    </IonPage>
  );
}
